"use client";

import { forwardRef } from "react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const Button = forwardRef(
  (
    {
      children,
      variant = "gradient",
      size = "md",
      className = "",
      type = "button",
      disabled = false,
      loading = false,
      iconLeft,
      iconRight,
      fullWidth = false,
      onClick,
      ...props
    },
    ref
  ) => {
    const base =
      "inline-flex items-center justify-center gap-2 font-medium rounded-2xl whitespace-nowrap select-none " +
      "transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 " +
      "disabled:cursor-not-allowed";

    const variants = {
      gradient:
        "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-600 " +
        "text-white shadow-2xl shadow-blue-900/30 ring-1 ring-blue-500/10",
      primary:
        "bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500",
      secondary:
        "bg-white hover:bg-gray-50 text-primary-600 border border-primary-600 focus:ring-primary-500",
      outline:
        "bg-transparent hover:bg-primary-50 text-primary-600 border border-primary-200 focus:ring-primary-500",
      ghost:
        "bg-transparent text-primary-600 hover:bg-gray-50 focus:ring-primary-500",
    };

    const sizes = {
      sm: "px-4 py-2 text-sm",
      md: "px-6 py-3 text-base",
      lg: "px-8 py-4 text-lg",
      xl: "px-12 py-6 text-xl md:text-2xl", // for hero CTAs
    };

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        aria-busy={loading ? "true" : undefined}
        className={cx(
          base,
          variants[variant],
          sizes[size],
          fullWidth && "w-full",
          className
        )}
        {...props}>
        {loading && (
          <svg
            className="h-5 w-5 animate-spin -ml-1"
            viewBox="0 0 24 24"
            aria-hidden="true"
            fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              d="M4 12a8 8 0 0 1 8-8"
              stroke="currentColor"
              strokeWidth="4"
            />
          </svg>
        )}
        {iconLeft && <span className="-ml-1">{iconLeft}</span>}
        <span>{children}</span>
        {iconRight && <span className="-mr-1">{iconRight}</span>}
      </button>
    );
  }
);

export default Button;
